import { AxiosRequestConfig } from 'axios'
import { FormEvent, useCallback, useEffect, useRef, useState } from 'react'

import { UserAndCompany } from '@/lib/definitions/api.types'
import { PortalDefaultTitle } from '@/lib/definitions/constants'
import { GenericErrors, GenericObject } from '@/lib/definitions/generic.types'
import { Notify } from '@/lib/misc/Notify'
import { CommittedDataRateOption } from '@/pages/ServiceWizard/ServiceWizard.types'
import { clsx, type ClassValue } from 'clsx'
import { twMerge } from 'tailwind-merge'

export const useFormInit = (dataObject: object) => {
  const [form, setForm] = useState(dataObject)
  const [errors, setErrors] = useState<GenericErrors>({})

  const handleChange = (e: FormEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.currentTarget

    setForm((data: object) => ({
      ...data,
      [name]: type === 'checkbox' ? checked : value,
    }))
  }

  const updateFormFields = useCallback(
    (fields: object) => setForm((formFields) => ({ ...formFields, ...fields })),
    []
  )

  return {
    handleChange,
    form,
    setForm,
    errors,
    setErrors,
    updateFormFields,
  }
}

/**
 * @deprecated Use cn instead
 * @param classes
 * @returns string
 */
export function classNames(...classes: any[]): string {
  return classes
    .map((item) => {
      if (typeof item === 'string') {
        return item
      }
      if (typeof item === 'object') {
        return Object.keys(item)
          .filter((key) => item[key])
          .join(' ')
      }
      return false
    })
    .filter(Boolean)
    .join(' ')
}

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

export const encodeQueryParams = (params: GenericObject): string =>
  Object.entries(params)
    .filter(
      ([, value]) => value !== undefined && value !== null && value !== ''
    )
    .map((param) => param.join('='))
    .join('&')

function getQueryParams(params?: GenericObject): string {
  return params ? `?${encodeQueryParams(params)}` : ''
}

export function apiUrl(path: string, params?: GenericObject): string {
  const host = `${import.meta.env.VITE_PORTAL_API_HOST || ''}`
  return `${host}${path}${getQueryParams(params)}`
}

export function dot(data: GenericObject, key?: string): any {
  if (!key || !data) {
    return data
  }

  if (!key.includes('.') && typeof data === 'object') {
    return data[key]
  }

  const keys: string[] = key.split('.')
  let nwObj = data

  for (let i = 0; i < keys.length; i++) {
    if (typeof nwObj === 'undefined' || !nwObj) {
      return null
    }
    nwObj = nwObj[keys[i]]
  }

  return nwObj
}

import { getAccessToken } from '@/lib/auth/authSession'

export function getToken(): string | null {
  return getAccessToken()
}

export const getAxiosRequestConfigWithHeaders = (
  headers?: Record<string, string>,
  options?: AxiosRequestConfig,
  tenant: boolean = true
): AxiosRequestConfig => {
  const token = getToken()

  if (!token) return {}

  const defaultHeaders = {
    Authorization: `Bearer ${token}`,
    ...(localStorage.getItem('customerId') &&
      tenant && {
        'AS-TENANT': localStorage.getItem('customerId'),
      }),
    ...headers,
  }

  return {
    headers: defaultHeaders,
    ...options,
  }
}

export function getCookies(): GenericObject {
  const localCookies = document.cookie.split(';')
  const cookies: GenericObject = {}

  localCookies.forEach((localCookie) => {
    const [key, value] = localCookie.split('=')
    cookies[key] = value
  })

  return cookies
}

export function numberFormat(
  number: number,
  { decimals = 2, decimalSeparator = ',', thousandSeparator = '.' } = {}
) {
  const n = !Number.isFinite(+number) ? 0 : +number,
    precision = !Number.isFinite(+decimals) ? 0 : Math.abs(decimals),
    toFixedFix = function toFixedFix(n: number, prec: number) {
      // Fix for IE parseFloat(0.55).toFixed(0) = 0;
      const k = 10 ** prec
      return Math.round(n * k) / k
    },
    s = (precision ? toFixedFix(n, precision) : Math.round(n))
      .toString()
      .split('.')

  if (s[0].length > 3) {
    s[0] = s[0].replace(/\B(?=(?:\d{3})+(?!\d))/g, thousandSeparator)
  }

  if ((s[1] || '').length < precision) {
    s[1] = s[1] || ''
    s[1] += Array.from({ length: precision - s[1].length }, () => '0').join('')
  }

  return s.join(decimalSeparator)
}

export function price(amount: number, labelIfZero?: string): string {
  return amount === 0 && labelIfZero ? labelIfZero : `€${numberFormat(amount)}`
}

export function formatContractTerm(numberOfMonths: number): string {
  return `${numberOfMonths} ${numberOfMonths === 1 ? 'month' : 'months'}`
}

export function getContractNoticePeriod(contractTermMonths: number): number {
  return contractTermMonths === 1 ? 14 : 90
}

export function getPaymentTermsDays(user: UserAndCompany): string {
  const days = user?.company?.account_payment_terms_days ?? 30

  if (user.company?.needs_to_pay_upfront) {
    return 'Prepayment'
  }

  return `${days.toString()} days`
}

export const formatDate = (date: number | string): string => {
  const options: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  }

  return new Date(date).toLocaleDateString('en-US', options)
}

export const formatDateTime = (date: number | string): string => {
  const options: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
  }

  return new Date(date).toLocaleDateString('en-US', options)
}

export const formatAddress = (address: {
  line1: string
  postal_code: string
  city: string
  country: string
}): string =>
  `${address.line1}, ${address.postal_code} ${address.city}, ${address.country}`

export const shortenText = (text: string, maxLength: number) =>
  text.length > maxLength ? `${text.slice(0, maxLength - 3)}...` : text

export const parseResponseErrorsAndNotify = (
  error: GenericObject,
  serverErrorMessage?: string,
  notifyError: boolean = true
) => {
  if (!error?.response) return

  const { data: responseData, headers: responseHeaders } = error.response
  const errorData = responseData.hasOwnProperty('detail')
    ? responseData.detail
    : responseData
  const errorStatus = error.response.status
  const errorMessages: string[] = []

  if (errorStatus >= 500) {
    errorMessages.push(serverErrorMessage ?? 'Server Error!')
    if (notifyError) {
      Notify.error(errorMessages[0])
    }
    return { messages: errorMessages, data: errorData }
  }

  if (
    typeof errorData === 'string' &&
    !responseHeaders['content-type'].includes('text/html')
  ) {
    errorMessages.push(errorData)
    if (notifyError) {
      Notify.error(errorMessages[0])
    }
    return { messages: errorMessages, data: errorData }
  }

  if (typeof errorData === 'object' && Object.values(errorData).length) {
    Object.values(errorData).forEach((err: any) => {
      if (typeof err === 'string') {
        errorMessages.push(err)
      }
      if (Array.isArray(err)) {
        errorMessages.push(err.join(', '))
      }
    })
    if (notifyError) {
      Notify.error(errorMessages.join(', '))
    }
    return { messages: errorMessages, data: errorData }
  }

  errorMessages.push("We couldn't process your request")
  if (notifyError) {
    Notify.error(errorMessages[0])
  }
  return { messages: errorMessages, data: errorData }
}

export const fieldsAreSet = (state: any, fields: string[]) =>
  fields
    .map((field) =>
      field
        .split('|')
        .map((optioalField) => {
          const value = dot(state, optioalField)
          return (
            value !== undefined && value !== null && value !== '' && value !== 0
          )
        })
        .includes(true)
    )
    .filter((item) => item).length === fields.length

export const areAllFieldsFilled = (state: any, fields: string[]) =>
  fields.some((field) => {
    const value = dot(state, field)
    return value !== undefined && value !== null && value !== ''
  })

export const alphanumericSortingFn = (key: string, a: any, b: any) =>
  a.getValue(key).label > b.getValue(key).label ? -1 : 1

export const timeBreakdown = (timeInSeconds: number) => {
  const days = Math.floor(timeInSeconds / 86400)
  let remainingSeconds = timeInSeconds % 86400

  const hours = Math.floor(remainingSeconds / 3600)
  remainingSeconds %= 3600

  const minutes = Math.floor(remainingSeconds / 60)
  const seconds = remainingSeconds % 60

  return {
    days,
    hours,
    minutes,
    seconds,
  }
}

export const generatePortalTitle = (title?: string): string =>
  title ? `${title} - ${PortalDefaultTitle}` : PortalDefaultTitle

export const scrollToTop = (wrapperRef: any) =>
  wrapperRef?.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })

export const scrollToWizardField = (fieldId: string) => {
  const section = document.getElementById(`wizard-field-${fieldId}`)
  if (!section) return

  section.scrollIntoView({ behavior: 'smooth', block: 'center' })

  const control = section.querySelector<HTMLElement>(
    'input:not([type="hidden"]):not([disabled]), select:not([disabled]), textarea:not([disabled])'
  )
  control?.focus({ preventScroll: true })
}

export const validateVLanId = (val: number) => {
  if (Number.isNaN(val)) {
    return 'VLAN ID should be a number'
  }
  if (val < 1) {
    return 'VLAN ID can not be a smaller than 1'
  }
  if (val > 4094) {
    return 'VLAN ID can not be greater than 4096'
  }
}

export const addBaseUrl = (path: string) => apiUrl(`/api/v1/${path}`)

export const formatSpeed = (mbps: number): string =>
  mbps < 1000 ? `${mbps} Mbps` : `${mbps / 1000} Gbps`

export const formatBytes = (bytes: number, decimals = 2): string => {
  // bytes, kilobytes, megabytes, gigabytes, terabytes, petabytes, exabytes, zettabytes, yottabytes
  const sizes = [
    'b/s',
    'kb/s',
    'Mb/s',
    'Gb/s',
    'Tb/s',
    'Pb/s',
    'Eb/s',
    'Zb/s',
    'Yb/s',
  ]
  if (bytes === 0) return '0 b/s'
  const i = Math.floor(Math.log(bytes) / Math.log(1024))
  if (i === 0) return `${bytes.toFixed(decimals)} ${sizes[i]}`
  return `${(bytes / 1024 ** i).toFixed(decimals)} ${sizes[i]}`
}

export const formatPackets = (packets: number, decimals = 2): string => {
  const sizes = [
    'p/s',
    'kp/s',
    'Mp/s',
    'Gp/s',
    'Tp/s',
    'Pp/s',
    'Ep/s',
    'Zp/s',
    'Yp/s',
  ]
  if (packets === 0) return '0 p/s'
  const i = Math.floor(Math.log(packets) / Math.log(1000))
  if (i === 0) return `${packets.toFixed(decimals)} ${sizes[i]}`
  return `${(packets / 1000 ** i).toFixed(decimals)} ${sizes[i]}`
}

export const generateBandwidthsData = (
  speedInMbps: number
): CommittedDataRateOption => ({
  speedInMbps,
  speed: speedInMbps >= 1000 ? speedInMbps / 1000 : speedInMbps,
  unit: speedInMbps >= 1000 ? 'Gbps' : 'Mbps',
})

export const useDebounce = (value: any, delay = 500) => {
  const [debouncedValue, setDebouncedValue] = useState(value)
  const timerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)

  useEffect(() => {
    timerRef.current = setTimeout(() => setDebouncedValue(value), delay)

    return () => {
      if (timerRef.current !== undefined) {
        clearTimeout(timerRef.current)
      }
    }
  }, [value, delay])

  return debouncedValue
}

export const haversineDistance = (
  lat1: number,
  lon1: number,
  lat2: number,
  lon2: number
): number => {
  const degToRad = (deg: number): number => deg * (Math.PI / 180)
  const R = 6371000 // Hearth radius
  const dLat = degToRad(lat2 - lat1)
  const dLon = degToRad(lon2 - lon1)
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(degToRad(lat1)) *
      Math.cos(degToRad(lat2)) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2)
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
  return R * c
}

export const notifyExtensionsTermChange = () => {
  Notify.warn(
    'The 1 month term is not available for Exstenions. Reverting to 12 months term'
  )
}

export const renderLagMaxSpeedValue = (
  speed: number,
  unit: string,
  memberCount: number
): string =>
  `${speed * memberCount} ${unit} (${memberCount} x ${speed} ${unit})`

export const getInitials = (name: string, length: number = 2): string =>
  name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, length)

export const secondsSince = (date: string): number => {
  const d = new Date(date)
  const now = new Date()

  return Math.floor((now.getTime() - d.getTime()) / 1000)
}

export const copyToClipboard = (text: string) => {
  navigator.clipboard.writeText(text)
  Notify.success('Copied to clipboard')
}

/**
 * Only validates the structure of the IP address, not if it is a valid public IP address.
 */
export const isValidIPv4HostAddress = (ip: string) => {
  const parts = ip.split('.')
  if (parts.length !== 4) {
    return false
  }

  for (const part of parts) {
    if (!/^\d+$/.test(part)) {
      return false
    }

    const num = Number(part)

    if (num < 0 || num > 255) {
      return false
    }
    if (part.length > 1 && part.startsWith('0')) {
      return false
    }
  }

  return true
}

export const isValidIPv4PublicAddress = (ip: string) => {
  if (!isValidIPv4HostAddress(ip)) {
    return false
  }

  const parts = ip.split('.').map((p) => Number(p))
  const [a, b, c] = parts

  if (a === 10) return false // 10.0.0.0/8
  if (a === 172 && b >= 16 && b <= 31) return false // 172.16.0.0/12
  if (a === 192 && b === 168) return false // 192.168.0.0/16
  if (a === 100 && b >= 64 && b <= 127) return false // 100.64.0.0/10
  if (a === 169 && b === 254) return false // 169.254.0.0/16
  if (a === 0) return false // 0.0.0.0/8
  if (a === 127) return false // 127.0.0.0/8
  if (a === 192 && b === 0 && c === 0) return false // 192.0.0.0/24
  if (a === 192 && b === 0 && c === 2) return false // 192.0.2.0/24
  if (a === 192 && b === 88 && c === 99) return false // 192.88.99.0/24
  if (a === 198 && b >= 18 && b <= 19) return false // 198.18.0.0/15
  if (a === 198 && b === 51 && c === 100) return false // 198.51.100.0/24
  if (a === 203 && b === 0 && c === 113) return false // 203.0.113.0/24
  if (a >= 224 && a <= 239) return false // 224.0.0.0/4
  if (a >= 240) return false // 240.0.0.0/4

  return true
}

export const formatRelativeFromIso = (iso: string): string | null => {
  const ms = new Date(iso).getTime()
  if (Number.isNaN(ms)) return null
  const diffSec = Math.round((ms - Date.now()) / 1000)
  const rtf = new Intl.RelativeTimeFormat(undefined, { numeric: 'auto' })
  const abs = Math.abs(diffSec)
  if (abs < 45) return 'just now'
  try {
    if (abs < 60) return rtf.format(diffSec < 0 ? -0 : 0, 'second')
    if (abs < 3600) return rtf.format(Math.round(diffSec / 60), 'minute')
    if (abs < 86400) return rtf.format(Math.round(diffSec / 3600), 'hour')
    if (abs < 604800) return rtf.format(Math.round(diffSec / 86400), 'day')
    if (abs < 2629800) return rtf.format(Math.round(diffSec / 604800), 'week')
    return rtf.format(Math.round(diffSec / 2629800), 'month')
  } catch {
    return null
  }
}

export const formatRelativeShortFromIso = (iso: string): string | null => {
  const ms = new Date(iso).getTime()
  if (Number.isNaN(ms)) return null
  const sec = Math.max(0, Math.round((Date.now() - ms) / 1000))
  if (sec < 60) return 'now'
  if (sec < 3600) return `${Math.floor(sec / 60)}m`
  if (sec < 86400) return `${Math.floor(sec / 3600)}h`
  if (sec < 604800) return `${Math.floor(sec / 86400)}d`
  if (sec < 2629800) return `${Math.floor(sec / 604800)}w`
  return `${Math.floor(sec / 2629800)}mo`
}

export const formatRelativeTime = (dateStr: string): string => {
  const diffMs = Date.now() - new Date(dateStr).getTime()
  const diffMins = Math.floor(diffMs / 60000)
  if (diffMins < 1) return 'just now'
  if (diffMins < 60) return `${diffMins}m ago`
  const diffH = Math.floor(diffMins / 60)
  if (diffH < 24) return `${diffH}h ago`
  const diffD = Math.floor(diffH / 24)
  if (diffD < 30) return `${diffD}d ago`
  const diffMo = Math.floor(diffD / 30)
  return `${diffMo}mo ago`
}

export function trimStr(v: unknown): string | null {
  if (typeof v !== 'string') return null
  const t = v.trim()
  return t.length ? t : null
}
